import { useState, useCallback } from 'react';
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import Tab from "@mui/material/Tab";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import styles from "./styles";
import { withStyles } from 'tss-react/mui';
const contextOffset = (event) => ({
  mouseX: event.clientX - 2,
  mouseY: event.clientY - 4,
});

const contextAnchor = (contextMenu) => ({
  top: contextMenu.mouseY,
  left: contextMenu.mouseX,
});

const CustomTab = ({ classes, value, label, to, ...props }) => {
  const [contextMenu, setContextMenu] = useState(null);

  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      setContextMenu(contextMenu === null ? contextOffset(event) : null);
    },
    [contextMenu]
  );

  const handleClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleOpenInNewTab = useCallback(() => {
    if (!to) return;
    window.open(to, "_blank");
    handleClose();
  }, [to, handleClose]);

  const handleMouseDown = useCallback(
    (event) => {
      if (!to) return;
      if (event.button === 1) {
        // Middle mouse button (button 1)
        event.preventDefault();
        window.open(to, "_blank");
      }
    },
    [to]
  );

  // Routing-related props are ONLY applied when an explicit `to`
  // is provided. The top navbar opts in by passing
  // `to={'/' + tab.value}`; the seafarer-card sub-tabs (rendered
  // through `<PageSubTab>`) leave `to` undefined so they remain
  // plain MUI `<Tab>` buttons whose selection is driven by the
  // parent `<Tabs onChange>`. Pre-fix the RouterLink was always
  // on and the sub-tabs ended up with `href="/0"`, `/1`, …
  // because MUI auto-assigns the child index as `value` when the
  // child has none (2026-05-20 follow-up to the navbar
  // same-section-click fix).
  const routingProps = to
    ? { component: RouterLink, to, onContextMenu: handleContextMenu, onMouseDown: handleMouseDown }
    : {};

  return (
    <>
      <Tab
        disableRipple
        classes={classes}
        value={value}
        label={label}
        {...routingProps}
        {...props}
      />
      {to && (
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? contextAnchor(contextMenu) : undefined
          }
        >
          <MenuItem onClick={handleOpenInNewTab}>Open in new tab</MenuItem>
        </Menu>
      )}
    </>
  );
};

CustomTab.propTypes = {
  classes: PropTypes.object.isRequired,
  // `value` is what MUI's `<Tabs>` uses to mark the selected tab.
  // Sub-tabs let MUI assign the index automatically, so this is
  // optional from CustomTab's point of view.
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  // Optional client-side route. Presence flips this Tab into
  // RouterLink mode (with context-menu + middle-click "open in
  // new tab" support); absence keeps it a plain Tab.
  to: PropTypes.string,
};

export default withStyles(CustomTab, styles);
